/// <reference types="segment-analytics" />
import * as React from 'react';
import useSetSnippet from '~/libs/analytics/hooks/useSetSnippet';
import { useMe } from '~/libs/api-hooks/api/private';
import { DataType } from '~/libs/api-hooks/tools';
import { useEffect, useMemo, useState } from 'react';
import {
  EventProperties,
  EventPropertiesSnakeCase,
} from '~/libs/analytics/types/EventProperties';
import mapCamelCaseObjectToSnakeCase from '~/libs/analytics/utils/mapCamelCaseObjectToSnakeCase';
import exceptionLogger from '~/libs/exception-logger';
import useSetupServer from '~/libs/analytics/hooks/useSetupServer';
import generateCircleciAnonymousId from '~/libs/analytics/utils/generateCircleciAnonymousId';
import getGoogleClientId from '~/libs/analytics/utils/getGoogleClientId';

export interface ServerEvents {
  page: (
    pageName: string,
    properties: Record<string, string | number | boolean | null>,
  ) => void;
  track: (
    eventName: string,
    properties: Record<string, string | number | boolean | null>,
  ) => void;
}

export interface AnalyticsContextType {
  segment: SegmentAnalytics.AnalyticsJS | undefined;
  server: ServerEvents | undefined;
  circleciAnonymousId: string | null;
  googleClientId: string | null;
}

export const AnalyticsContext = React.createContext<
  AnalyticsContextType | null | undefined
>(undefined);

interface AnalyticsProps {
  isOuterPage?: boolean;
  children?: any;
}

/**
 * Sets up segement analytics and send identify event.
 */
const Analytics = ({ isOuterPage = false, children }: AnalyticsProps) => {
  const {
    segment,
    isAnalyticsLoadSuccess,
    isError: isSetSnippetError,
  } = useSetSnippet(isOuterPage);
  const { server } = useSetupServer(isOuterPage);
  const { data: me, isSuccess: isMeSuccess } = useMe({
    reactQueryOptions: {
      retry: false,
      retryOnMount: false,
      notifyOnChangeProps: ['isSuccess'],
      enabled: !isOuterPage,
    },
  });

  const [circleciAnonymousId, setCircleciAnonymousId] = useState<string | null>(
    null,
  );
  const googleClientId = getGoogleClientId();

  // useEffect is to insure that the client has loaded before access localstorage
  useEffect(() => {
    setCircleciAnonymousId(generateCircleciAnonymousId());
  }, []);

  useEffect(() => {
    if (
      isOuterPage ||
      !isMeSuccess ||
      !me ||
      !isAnalyticsLoadSuccess ||
      !circleciAnonymousId
    ) {
      return;
    }
    TrackUser({ analytics: segment, me, circleciAnonymousId, googleClientId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMeSuccess, segment, isAnalyticsLoadSuccess, circleciAnonymousId]);

  const analyticsContext = useMemo(
    () => ({
      segment:
        isAnalyticsLoadSuccess && !isSetSnippetError ? segment : undefined,
      server,
      circleciAnonymousId,
      googleClientId,
    }),
    [
      segment,
      isAnalyticsLoadSuccess,
      isSetSnippetError,
      server,
      circleciAnonymousId,
      googleClientId,
    ],
  );

  return (
    <AnalyticsContext.Provider value={analyticsContext}>
      {children}
    </AnalyticsContext.Provider>
  );
};

const TrackUser = ({
  analytics,
  me,
  circleciAnonymousId,
  googleClientId,
}: {
  analytics: SegmentAnalytics.AnalyticsJS | undefined;
  me: DataType<typeof useMe>;
  circleciAnonymousId: string | null;
  googleClientId: string | null;
}) => {
  const name = me.analyticsId;
  if (!name) return;
  const traits: Partial<EventProperties> = {
    circleciAnonymousId,
    googleClientId,
  };
  Object.keys(me).forEach((property) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (['string', 'number', 'boolean'].includes(typeof me[property])) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      traits[property] = me[property];
    }
  });

  try {
    const traitsSnaked =
      mapCamelCaseObjectToSnakeCase<EventPropertiesSnakeCase>(traits);
    analytics?.identify(name, traitsSnaked);
  } catch (error: any) {
    exceptionLogger().error('Track User', name, traits, error);
  }
};

export default Analytics;
